import { useEffect, useState } from 'react';
import { Loader2, AlertCircle, BarChart3 } from 'lucide-react';
import { MermaidDiagram } from '../common/MermaidDiagram';
import { issueAnalysisApi } from '../../services/issueAnalysisApi';

interface RcaVisualizationPanelProps {
  analysisId: number;
}

export function RcaVisualizationPanel({ analysisId }: RcaVisualizationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gantt, setGantt] = useState<string | null>(null);
  const [causalChain, setCausalChain] = useState<string | null>(null);
  const [actionFlow, setActionFlow] = useState<string | null>(null);

  useEffect(() => {
    const fetchVisualizations = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await issueAnalysisApi.generateRcaVisualizations(analysisId);
        setGantt(result.gantt);
        setCausalChain(result.causal_chain);
        setActionFlow(result.action_flow);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load visualizations');
        setGantt(null);
        setCausalChain(null);
        setActionFlow(null);
      } finally {
        setLoading(false);
      }
    };

    fetchVisualizations();
  }, [analysisId]);

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2.5 bg-gray-50 flex items-center gap-2">
          <BarChart3 size={14} className="text-purple-600" />
          <span className="text-xs font-semibold text-gray-800">RCA Visualizations</span>
        </div>
        <div className="px-3 pb-3 pt-2 border-t border-gray-100 flex items-center justify-center py-8">
          <Loader2 size={16} className="text-gray-400 animate-spin mr-2" />
          <span className="text-xs text-gray-500">Loading visualizations...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2.5 bg-gray-50 flex items-center gap-2">
          <BarChart3 size={14} className="text-purple-600" />
          <span className="text-xs font-semibold text-gray-800">RCA Visualizations</span>
        </div>
        <div className="px-3 pb-3 pt-2 border-t border-gray-100">
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700 flex items-start gap-2">
            <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Visualizations unavailable</p>
              <p className="text-xs mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!gantt && !causalChain && !actionFlow) {
    return null;
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2.5 bg-gray-50 flex items-center gap-2">
        <BarChart3 size={14} className="text-purple-600" />
        <span className="text-xs font-semibold text-gray-800">RCA Visualizations</span>
      </div>
      <div className="px-3 pb-3 pt-2 border-t border-gray-100 space-y-4">
        {gantt && (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Timeline</h4>
            <MermaidDiagram chart={gantt} zoomable title="RCA Timeline" />
          </div>
        )}
        {causalChain && (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Causal Chain</h4>
            <MermaidDiagram chart={causalChain} zoomable title="RCA Causal Chain" textSize={10} />
          </div>
        )}
        {actionFlow && (
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">Action Plan</h4>
            <MermaidDiagram chart={actionFlow} zoomable title="RCA Action Plan" textSize={10} />
          </div>
        )}
      </div>
    </div>
  );
}
